/**
 * CatalogReviewPage — /agents/libraries/catalog-review
 * The catalog review workspace on the DetailPage template. Demo page: /email (inbox beside a reading pane) with
 * `/tables/basic-tables` for the audit tabs. The tab is kept in the URL (`?tab=candidates|sources|versions|adoptions|queue`).
 * Split by responsibility in ./catalog-review/: data and actions in `useCatalogReview`, one file per tab section,
 * pure helpers in `catalog-helpers` (re-exported below for the tests and other importers).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Callout, QueryBoundary, Skeleton } from '../../components/ui/index.js';
import { DetailPage } from '../../components/page/index.js';
import { buttonStyles } from '../../components/tailgrids/core/button.js';
import { AdoptionTable } from './catalog-review/AdoptionTable.js';
import { CandidatesTab } from './catalog-review/CandidatesTab.js';
import { CatalogReviewSummary } from './catalog-review/CatalogReviewSummary.js';
import { ReviewQueueList } from './catalog-review/ReviewQueueList.js';
import { SourceTable } from './catalog-review/SourceTable.js';
import { VersionTable } from './catalog-review/VersionTable.js';
import { libraryItemPath, typeLabel } from './catalog-review/catalog-helpers.js';
import { useCatalogReview } from './catalog-review/useCatalogReview.js';

export {
  buildCatalogSourceEvidenceHref,
  catalogSourceFragmentLabel,
  describeCatalogCandidateIntegrity,
  describeCatalogSourceIntegrity,
  summarizeCatalogReviewAudit,
} from './catalog-review/catalog-helpers.js';

export const CatalogReviewPage: React.FC = () => {
  const review = useCatalogReview();
  const { queries, actions, busy, filters, lastPromotion } = review;
  const candidates = queries.candidates.data?.data ?? [];
  const sources = queries.sources.data?.data ?? [];
  const versions = queries.versions.data?.data ?? [];
  const queueItems = queries.queue.data?.data ?? [];

  const promotedPath = lastPromotion
    ? libraryItemPath(
        lastPromotion.materializedTarget.type,
        lastPromotion.materializedTarget.id,
        lastPromotion.materializedTarget.label,
      )
    : null;
  const filterLabel = filters.activeTargetTypes.map(typeLabel).join(' + ');

  const tabs = [
    { id: 'candidates', label: 'Candidates', count: candidates.length },
    { id: 'sources', label: 'Sources', count: sources.length },
    { id: 'versions', label: 'Versions', count: versions.length },
    { id: 'adoptions', label: 'Adoptions' },
    { id: 'queue', label: 'Review queue', count: queueItems.length },
  ];

  return (
    <DetailPage
      title="Catalog review"
      subtitle="Review one source-backed candidate at a time. Use daemon findings, provenance and plain-language value before promoting anything into the usable libraries."
      breadcrumbs={[
        { label: 'Libraries', to: '/agents/libraries' },
        { label: 'Catalog review', ...(filterLabel ? { to: '/agents/libraries/catalog-review' } : {}) },
        ...(filterLabel ? [{ label: filterLabel }] : []),
      ]}
      alert={
        <div className="space-y-5">
          <CatalogReviewSummary
            candidates={candidates}
            sources={sources}
            queueItems={queueItems}
            versions={versions}
            onTabChange={review.setTab}
            onRetryReviews={() => actions.retryQueue(undefined)}
            onProcessReviews={() => actions.processQueue(undefined)}
            onShowFailedCandidates={() => filters.showStatuses(['security_failed', 'quality_failed'])}
            busy={busy.summary}
          />
          {lastPromotion && (
            <Callout
              tone="success"
              title={`Promoted version ${lastPromotion.version.version}`}
              actions={
                promotedPath ? (
                  <Link
                    to={promotedPath}
                    className={buttonStyles({ variant: 'primary', appearance: 'outline', size: 'sm' })}
                  >
                    Open promoted library item <ChevronRight size={13} aria-hidden="true" />
                  </Link>
                ) : undefined
              }
            >
              Created {lastPromotion.materializedTarget.type.replaceAll('_', ' ')} #
              {lastPromotion.materializedTarget.id}: {lastPromotion.materializedTarget.label}
              {lastPromotion.warnings.length > 0 && (
                <span className="mt-1 block text-xs">{lastPromotion.warnings.join(' ')}</span>
              )}
            </Callout>
          )}
        </div>
      }
      tabs={tabs}
      activeTab={review.tab}
      onTabChange={review.setTab}
    >
      {review.tab === 'candidates' && <CandidatesTab review={review} />}
      {review.tab === 'sources' && (
        <QueryBoundary query={queries.sources} label="sources" loading={<Skeleton height={160} />}>
          {(result) => <SourceTable sources={result.data} />}
        </QueryBoundary>
      )}
      {review.tab === 'versions' && (
        <QueryBoundary query={queries.versions} label="versions" loading={<Skeleton height={160} />}>
          {(result) => (
            <VersionTable
              versions={result.data}
              onRestore={actions.restoreVersion}
              restoringVersionId={busy.restoringVersionId}
            />
          )}
        </QueryBoundary>
      )}
      {review.tab === 'adoptions' && (
        <QueryBoundary query={queries.adoptions} label="adoptions" loading={<Skeleton height={160} />}>
          {(result) => (
            <AdoptionTable
              adoptions={result.data}
              onUpgrade={actions.upgrade}
              onRevert={actions.revert}
              upgradingKey={busy.upgradingKey}
              revertingKey={busy.revertingKey}
            />
          )}
        </QueryBoundary>
      )}
      {review.tab === 'queue' && (
        <QueryBoundary query={queries.queue} label="the review queue" loading={<Skeleton height={160} />}>
          {(result) => (
            <ReviewQueueList
              items={result.data}
              onOpenCandidate={(candidateId) => {
                review.selectCandidate(candidateId);
                review.setTab('candidates');
              }}
              onEnableDaemon={actions.enableAndRetry}
              onRetryItem={actions.retryItem}
              onRetryDaemon={actions.retryQueue}
              onProcessDaemon={actions.processQueue}
              busy={busy.queue}
            />
          )}
        </QueryBoundary>
      )}
    </DetailPage>
  );
};
